import React, { useState } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogContent,
  IconButton,
  CircularProgress,
  Skeleton
} from '@mui/material';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import CloseIcon from '@mui/icons-material/Close';

/**
 * 格式化影片時長
 * @param {number} seconds - 秒數
 * @returns {string} 格式化的時長 (MM:SS)
 */
export const formatDuration = (seconds) => {
  if (!seconds || isNaN(seconds)) return '';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

/**
 * 判斷媒體項目是否為影片
 * @param {Object|string} item - 媒體項目
 * @returns {boolean} 是否為影片
 */
export const isVideoMedia = (item) => {
  if (!item) return false;
  
  // 🔥 NEW: 如果是字串，檢查 URL 副檔名
  if (typeof item === 'string') {
    const lowerUrl = item.toLowerCase();
    return lowerUrl.includes('.mp4') || 
           lowerUrl.includes('.webm') || 
           lowerUrl.includes('.mov') ||
           lowerUrl.includes('.avi');
  }
  
  // 檢查 mimeType
  if (typeof item === 'object') {
    const mimeType = item.mimeType || item.metadata?.mimeType;
    if (mimeType && mimeType.startsWith('video/')) return true;
    
    // 檢查 metadata.mediaType
    if (item.metadata?.mediaType === 'video') return true;
    
    // 🔥 NEW: 檢查 URL 副檔名
    const url = item.url || item.s3Url || item.imageUrl || '';
    const lowerUrl = url.toLowerCase();
    if (lowerUrl.includes('.mp4') || 
        lowerUrl.includes('.webm') || 
        lowerUrl.includes('.mov') ||
        lowerUrl.includes('.avi')) {
      return true;
    }
  }
  
  return false;
};

/**
 * 影片縮圖元件 - 顯示影片預覽、播放按鈕、時長
 */
export const VideoThumbnail = ({ 
  videoData, 
  videoId = 'main',
  metadata = {},
  onClick,
  maxWidth = '300px'
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // 🔥 ENHANCED: 取得影片資料 - 多層級提取 URL
  let videoUrl, thumbnailUrl, duration, videoMeta;
  
  if (typeof videoData === 'object') {
    // 🔥 從多個可能的位置提取影片 URL
    videoUrl = videoData.url || 
               videoData.s3Url || 
               videoData.imageUrl ||
               videoData.videoUrl ||
               videoData.metadata?.s3Url ||
               videoData.metadata?.url ||
               videoData.metadata?.videoUrl ||
               metadata?.s3Url ||
               metadata?.url ||
               metadata?.videoUrl;
    
    // 提取縮圖 URL
    thumbnailUrl = videoData.thumbnailUrl || 
                   videoData.thumbnail || 
                   videoData.metadata?.thumbnailUrl ||
                   videoData.metadata?.thumbnail ||
                   metadata?.thumbnailUrl ||
                   metadata?.thumbnail;
    
    // 提取時長
    duration = videoData.durationSeconds ||
               videoData.metadata?.durationSeconds || 
               metadata?.durationSeconds ||
               (videoData.duration ? Math.floor(videoData.duration / 1000) : null) ||
               (videoData.metadata?.duration ? Math.floor(videoData.metadata.duration / 1000) : null);
    
    videoMeta = videoData.metadata || videoData;
    
  } else {
    videoUrl = videoData;
    thumbnailUrl = metadata?.thumbnailUrl || metadata?.thumbnail;
    duration = metadata?.durationSeconds;
    videoMeta = metadata || {};
  }

  const handleLoad = () => {
    setLoading(false);
  };

  const handleError = () => {
    setLoading(false);
    setError(true);
  };

  const handleClick = () => {
    if (!error && onClick) {
      const clickData = {
        url: videoUrl,
        thumbnailUrl: thumbnailUrl,
        duration: duration,
        filename: videoMeta?.filename || videoMeta?.name || '影片',
        metadata: videoMeta
      };
      
      onClick(clickData);
    }
  };

  return (
    <Box
      sx={{
        position: 'relative',
        borderRadius: 1,
        overflow: 'hidden',
        cursor: onClick ? 'pointer' : 'default',
        maxWidth: maxWidth,
        bgcolor: 'grey.900',
        '&:hover .video-overlay': {
          opacity: 1
        }
      }}
      onClick={handleClick}
    >
      {/* 載入中的骨架屏 */}
      {loading && !error && (
        <Skeleton
          variant="rectangular"
          width={300}
          height={200}
          animation="wave"
          sx={{ bgcolor: 'grey.800' }}
        />
      )}
      
      {/* 錯誤狀態 */}
      {error ? (
        <Box
          sx={{
            width: 300,
            height: 200,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'grey.800'
          }}
        >
          <VideocamOffIcon sx={{ fontSize: 48, color: 'grey.500', mb: 1 }} />
          <Typography variant="body2" color="grey.400">
            影片載入失敗
          </Typography>
        </Box>
      ) : (
        <>
          {/* 縮圖或佔位圖 */}
          {thumbnailUrl ? (
            <Box
              component="img"
              src={thumbnailUrl}
              alt="影片預覽"
              sx={{
                width: '100%',
                maxHeight: '300px',
                objectFit: 'cover',
                display: loading ? 'none' : 'block'
              }}
              onLoad={handleLoad}
              onError={handleError}
              crossOrigin="anonymous"
            />
          ) : (
            <Box
              sx={{
                width: 300,
                height: 180,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'grey.800'
              }}
              ref={() => {
                // 無縮圖時直接設為載入完成
                if (loading) setLoading(false);
              }}
            >
              <Typography variant="h2" sx={{ color: 'grey.600' }}>
                🎬
              </Typography>
            </Box>
          )}
          
          {/* 播放按鈕覆蓋層 */}
          <Box
            className="video-overlay"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(0, 0, 0, 0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: 0.8,
              transition: 'opacity 0.3s'
            }}
          >
            <PlayCircleFilledIcon 
              sx={{ 
                fontSize: 64, 
                color: 'white',
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))'
              }} 
            />
          </Box>
          
          {/* 時長標籤 */}
          {duration && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 8,
                right: 8,
                bgcolor: 'rgba(0, 0, 0, 0.7)',
                color: 'white',
                px: 1,
                py: 0.25,
                borderRadius: 0.5,
                fontSize: '0.75rem',
                fontWeight: 500
              }}
            >
              {formatDuration(duration)}
            </Box>
          )}
          
          {/* 影片標籤 */}
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              bgcolor: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              px: 1,
              py: 0.25,
              borderRadius: 0.5,
              fontSize: '0.7rem',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5
            }}
          >
            🎬 影片
          </Box>
        </>
      )}
    </Box>
  );
};

/**
 * 影片無資料時的佔位元件
 */
export const VideoPlaceholder = ({ content, metadata }) => {
  return (
    <Box
      sx={{
        width: 200,
        height: 120,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'grey.800',
        borderRadius: 1,
        color: 'grey.400'
      }}
    >
      <Typography variant="h4">🎬</Typography>
      <Typography variant="body2" sx={{ mt: 1 }}>
        {content || '[影片]'}
      </Typography>
      {metadata?.durationSeconds && (
        <Typography variant="caption" sx={{ mt: 0.5 }}>
          {formatDuration(metadata.durationSeconds)}
        </Typography>
      )}
    </Box>
  );
};

/**
 * 影片播放器 Modal
 */
export const VideoPlayerModal = ({ open, onClose, video }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // 重置狀態當 video 改變
  React.useEffect(() => {
    if (video) {
      setLoading(true);
      setError(false);
      
      // 🔥 檢查是否有影片 URL
      if (!video.url) {
        setError(true);
        setLoading(false);
      }
    }
  }, [video]);

  if (!video) return null;

  const handleVideoLoad = () => {
    setLoading(false);
  };

  const handleVideoError = (e) => {
    setLoading(false);
    setError(true);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'black',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogContent sx={{ p: 0, position: 'relative', bgcolor: 'black' }}>
        {/* 關閉按鈕 */}
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: 'white',
            bgcolor: 'rgba(0,0,0,0.5)',
            zIndex: 10,
            '&:hover': {
              bgcolor: 'rgba(0,0,0,0.7)'
            }
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* 載入中 */}
        {loading && !error && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 5
            }}
          >
            <CircularProgress sx={{ color: 'white' }} />
          </Box>
        )}

        {/* 錯誤狀態 */}
        {error ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 300,
              color: 'grey.400',
              p: 3
            }}
          >
            <VideocamOffIcon sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h6" sx={{ mb: 1 }}>影片無法播放</Typography>
            {!video.url ? (
              <Typography variant="body2" sx={{ color: 'error.main', textAlign: 'center' }}>
                找不到影片連結
              </Typography>
            ) : (
              <Typography variant="caption" sx={{ color: 'grey.600', textAlign: 'center', wordBreak: 'break-all', maxWidth: '90%' }}>
                URL: {video.url}
              </Typography>
            )}
          </Box>
        ) : (
          <Box
            component="video"
            src={video.url}
            poster={video.thumbnailUrl}
            controls
            autoPlay
            sx={{
              width: '100%',
              maxHeight: 'calc(90vh - 32px)',
              display: loading ? 'none' : 'block'
            }}
            onLoadedData={handleVideoLoad}
            onError={handleVideoError}
            crossOrigin="anonymous"
          />
        )}

        {/* 影片資訊 */}
        {!error && video.filename && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 60,
              left: 16,
              color: 'white',
              bgcolor: 'rgba(0,0,0,0.5)',
              px: 1.5,
              py: 0.5,
              borderRadius: 1,
              fontSize: '0.875rem'
            }}
          >
            {video.filename}
            {video.duration && ` • ${formatDuration(video.duration)}`}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

// 預設匯出所有元件
export default {
  formatDuration,
  isVideoMedia,
  VideoThumbnail,
  VideoPlaceholder,
  VideoPlayerModal
};